/**
 * Utility for managing booking flow data via URL query parameters
 * Similar to PHP's approach of storing form data in URL params
 */

/**
 * Serialize booking data to URL query parameters
 */
export const serializeBookingData = (data) => {
  const params = new URLSearchParams();

  // Flight search parameters
  if (data.from) params.set('from', data.from);
  if (data.to) params.set('to', data.to);
  if (data.departure) params.set('departure', data.departure);

  // Only add return date if it exists AND routeType is not "One Way"
  if (data.return && data.routeType !== 'One Way') {
    params.set('return', data.return);
  }

  if (data.passengers) params.set('passengers', data.passengers);
  if (data.tripType) params.set('tripType', data.tripType);
  if (data.routeType) params.set('routeType', data.routeType);

  // Flight details
  if (data.flight_id) params.set('flight_id', data.flight_id);
  if (data.return_flight_id) params.set('return_flight_id', data.return_flight_id);
  if (data.aircraft_id) params.set('aircraft_id', data.aircraft_id);
  if (data.registration_number) params.set('registration_number', data.registration_number);
  if (data.flight_number) params.set('flight_number', data.flight_number);
  if (data.departure_airport) params.set('departure_airport', data.departure_airport);
  if (data.departure_date_time) params.set('departure_date_time', data.departure_date_time);
  if (data.arrival_airport) params.set('arrival_airport', data.arrival_airport);
  if (data.arrival_date_time) params.set('arrival_date_time', data.arrival_date_time);
  if (data.selected_seat) params.set('selected_seat', data.selected_seat);
  if (data.amount) params.set('amount', data.amount);
  if (data.routes_id) params.set('routes_id', data.routes_id);

  // Pricing data
  if (data.farePerPax) params.set('farePerPax', data.farePerPax);
  if (data.totalFare) params.set('totalFare', data.totalFare);
  if (data.blockFare) params.set('blockFare', data.blockFare);

  // Passenger data (stored as JSON in a single param)
  if (data.passengerData && Array.isArray(data.passengerData)) {
    params.set('passengerData', JSON.stringify(data.passengerData));
  }

  // Selected flights for multi-leg bookings (stored as JSON)
  if (data.selectedFlights && Array.isArray(data.selectedFlights)) {
    params.set('selectedFlights', JSON.stringify(data.selectedFlights));
  }

  // Payment data
  if (data.address) params.set('address', data.address);
  if (data.payment_method) params.set('payment_method', data.payment_method);

  // Save for future flag
  if (data.saveForFuture !== undefined) params.set('saveForFuture', data.saveForFuture);

  // Seat lock token
  if (data.booking_token) params.set('booking_token', data.booking_token);

  return params.toString();
};

/**
 * Deserialize URL query parameters to booking data object
 */
export const deserializeBookingData = (searchParams) => {
  const data = {};

  // Flight search parameters
  if (searchParams.get('from')) data.from = searchParams.get('from');
  if (searchParams.get('to')) data.to = searchParams.get('to');
  if (searchParams.get('departure')) data.departure = searchParams.get('departure');
  if (searchParams.get('return')) data.return = searchParams.get('return');
  if (searchParams.get('passengers')) data.passengers = parseInt(searchParams.get('passengers'));
  if (searchParams.get('tripType')) data.tripType = searchParams.get('tripType');
  if (searchParams.get('routeType')) data.routeType = searchParams.get('routeType');

  // Flight details
  if (searchParams.get('flight_id')) data.flight_id = parseInt(searchParams.get('flight_id'));
  if (searchParams.get('return_flight_id')) data.return_flight_id = parseInt(searchParams.get('return_flight_id'));
  if (searchParams.get('aircraft_id')) data.aircraft_id = parseInt(searchParams.get('aircraft_id'));
  if (searchParams.get('registration_number')) data.registration_number = searchParams.get('registration_number');
  if (searchParams.get('flight_number')) data.flight_number = searchParams.get('flight_number');
  if (searchParams.get('departure_airport')) data.departure_airport = parseInt(searchParams.get('departure_airport'));
  if (searchParams.get('departure_date_time')) data.departure_date_time = searchParams.get('departure_date_time');
  if (searchParams.get('arrival_airport')) data.arrival_airport = parseInt(searchParams.get('arrival_airport'));
  if (searchParams.get('arrival_date_time')) data.arrival_date_time = searchParams.get('arrival_date_time');
  if (searchParams.get('selected_seat')) data.selected_seat = parseInt(searchParams.get('selected_seat'));
  if (searchParams.get('amount')) data.amount = parseFloat(searchParams.get('amount'));
  if (searchParams.get('routes_id')) data.routes_id = parseInt(searchParams.get('routes_id'));

  // Pricing data
  if (searchParams.get('farePerPax')) data.farePerPax = parseFloat(searchParams.get('farePerPax'));
  if (searchParams.get('totalFare')) data.totalFare = parseFloat(searchParams.get('totalFare'));
  if (searchParams.get('blockFare')) data.blockFare = parseFloat(searchParams.get('blockFare'));

  // Passenger data
  if (searchParams.get('passengerData')) {
    try {
      data.passengerData = JSON.parse(searchParams.get('passengerData'));
    } catch (e) {
      console.error('Failed to parse passenger data:', e);
    }
  }

  // Selected flights for multi-leg bookings
  if (searchParams.get('selectedFlights')) {
    try {
      data.selectedFlights = JSON.parse(searchParams.get('selectedFlights'));
    } catch (e) {
      console.error('Failed to parse selected flights:', e);
    }
  }

  // Payment data
  if (searchParams.get('address')) data.address = searchParams.get('address');
  if (searchParams.get('payment_method')) data.payment_method = searchParams.get('payment_method');

  // Save for future flag
  if (searchParams.get('saveForFuture')) data.saveForFuture = searchParams.get('saveForFuture') === 'true';

  // Seat lock token
  if (searchParams.get('booking_token')) data.booking_token = searchParams.get('booking_token');

  return data;
};

/**
 * Build URL with booking data as query parameters
 */
export const buildBookingUrl = (basePath, bookingData) => {
  const queryString = serializeBookingData(bookingData);
  return queryString ? `${basePath}?${queryString}` : basePath;
};

/**
 * Navigate with booking data in query params
 */
export const navigateWithBookingData = (navigate, path, bookingData, options = {}) => {
  // Ensure path is absolute (starts with /)
  const absolutePath = path.startsWith('/') ? path : `/${path}`;
  const url = buildBookingUrl(absolutePath, bookingData);
  console.log('🔗 navigateWithBookingData:', { path, absolutePath, url, hasOptions: !!options });
  navigate(url, options);
};

/**
 * Get booking data from current URL
 */
export const getBookingDataFromUrl = (location) => {
  const searchParams = new URLSearchParams(location.search);
  return deserializeBookingData(searchParams);
};

/**
 * Merge booking data from URL and state
 * Priority: URL params > location.state
 */
export const mergeBookingData = (location) => {
  const urlData = getBookingDataFromUrl(location);
  const stateData = location.state || {};

  return {
    ...stateData,
    ...urlData,
    // Use data from URL or state only, no sessionStorage fallback
    passengerData: urlData.passengerData || stateData.passengers || null,
    passengers: urlData.passengers || stateData.totalPassengers || null,
    routeType: urlData.routeType || stateData.routeType || 'One Way'
  };
};
